/**
 * Text-to-Speech Utility
 * Provides clean, reusable text-to-speech functionality using Web Speech API
 */

// Module-level state
// eslint-disable-next-line no-unused-vars
let currentUtterance = null; // Tracks the current speech utterance for cleanup
let currentSessionCancelled = false; // Cancels any pending heading-pause timeouts
const isSupported = 'speechSynthesis' in window;

// Configuration constants
const MAX_TEXT_LENGTH = 50000; // Maximum text length to prevent browser crashes
const CHUNK_SIZE = 100; // Characters per chunk (reduced to prevent Chrome ~15s timeout glitch on dense numbers)

/**
 * Check browser and device compatibility
 * @returns {Object} - Compatibility information
 */
const getCompatibilityInfo = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isChrome = userAgent.includes('chrome') && !userAgent.includes('edg');
  const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');
  const isFirefox = userAgent.includes('firefox');
  const isEdge = userAgent.includes('edg');
  const isMobile = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

  return {
    isSupported,
    isChrome,
    isSafari,
    isFirefox,
    isEdge,
    isMobile,
    recommendation: getRecommendation(isChrome, isSafari, isFirefox, isEdge, isMobile)
  };
};

/**
 * Get recommendation based on browser/device
 */
const getRecommendation = (isChrome, isSafari, isFirefox, isEdge, isMobile) => {
  if (isFirefox) return 'limited'; // Firefox has very limited support
  if (isChrome || isEdge || isSafari) return 'excellent';
  if (isMobile && (isChrome || isSafari)) return 'good';
  return 'unknown';
};

/**
 * Stop any current speech
 */
const stop = () => {
  currentSessionCancelled = true; // Cancel any pending heading-pause timeouts
  if (isSupported && window.speechSynthesis.speaking) {
    window.speechSynthesis.cancel();
  }
  currentUtterance = null;
};

/**
 * Initialize page-level event handlers to manage speech across page reloads
 */
const initializePageHandlers = () => {
  if (!isSupported) return;

  // Stop speech immediately when page loads
  stop();

  // Stop speech when page is about to unload
  window.addEventListener('beforeunload', () => {
    stop();
  });

  // Stop speech when page becomes hidden (tab switch, minimize, etc.)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stop();
    }
  });

  // Stop speech when page loses focus
  window.addEventListener('blur', () => {
    stop();
  });
};

// Initialize handlers when module loads
initializePageHandlers();

/**
 * Clean text by removing HTML, markdown, emojis, and normalizing whitespace.
 * Preserves ### headings as a special delimiter (HEADING_SEP) so that
 * the caller can insert pauses between heading and body.
 * @param {string} text - Raw text content
 * @returns {string} - Cleaned text ready for speech
 */
const HEADING_SEP = '\u0000HEADING\u0000'; // internal sentinel

const cleanTextForSpeech = (text) => {
  if (!text || typeof text !== 'string') return '';

  let cleanText = text;

  // Remove HTML tags using DOMParser (safe from execution)
  if (typeof DOMParser !== 'undefined') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');

    // Remove all HTML tables so their content is completely skipped
    const tables = doc.querySelectorAll('table');
    tables.forEach(table => table.remove());

    cleanText = doc.body.textContent || "";
  }

  // Fix pronunciation for TalentPulse360
  cleanText = cleanText.replace(/TalentPulse360/gi, 'Talent Pulse three sixty');

  // Prevent TTS engines (especially macOS/iOS) from reading "TM" as "Trade Mark"
  cleanText = cleanText.replace(/\bTM\b/g, 'T M');
  cleanText = cleanText.replace(/\bTMs\b/g, 'T Ms');

  // Prevent reading "Streaming Stopped" out loud
  // cleanText = cleanText.replace(/Streaming Stopped/gi, '');

  // Format phone numbers to be read digit by digit
  // Matches 10 digit numbers with optional +91 or 91 country code and separators
  cleanText = cleanText.replace(/(?<!\d)(?:\+?91[\s-]?)?(?:\d[\s-]*){9}\d(?!\d)/g, (match) => {
    return match.replace(/[^\d+]/g, '').split('').join(' ');
  });

  // Remove emojis (they sound unnatural when spoken)
  cleanText = cleanText.replace(/[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\u{FE0F}\u{200D}]/gu, '');

  // Remove citations (e.g. [1], [1, 2], 【13†source】)
  cleanText = cleanText.replace(/\[\d+(?:\s*,\s*\d+)*\]/g, '');
  cleanText = cleanText.replace(/【[^】]*】/g, '');

  // Remove assistant extra messages (represented by empty headings like "### \n" and their content)
  cleanText = cleanText.replace(/#{1,6}\s*\n(?:(?!#{1,6})[\s\S])*/g, '');

  // Remove Markdown tables robustly
  // Matches consecutive lines containing '|'
  cleanText = cleanText.replace(/(?:^[ \t]*.*\|.*[ \t\r]*(?:\n|$))+/gm, (match) => {
    // Only remove if it contains a valid markdown table separator row (e.g. |---|---|)
    if (/^[ \t]*\|?[-: \t|]{3,}\|?[ \t\r]*$/m.test(match)) {
      return '';
    }
    // Also remove the simple case of lines starting and ending with |
    if (/^[ \t]*\|.*\|[ \t\r]*$/m.test(match)) {
      return '';
    }
    return match;
  });

  // Remove individual markdown table separator lines that might have been chunked alone
  cleanText = cleanText.replace(/^[ \t]*\|?[-: \t|]{3,}\|?[ \t\r]*$/gm, '');

  // Convert ALL CAPS words (4+ chars) to Title Case BEFORE inserting the heading sentinel,
  cleanText = cleanText.replace(/\b([A-Z])([A-Z]{3,})\b/g, (match, firstLetter, restOfWord) => {
    return firstLetter + restOfWord.toLowerCase();
  });

  // Replace ### headings with sentinel so we can pause after them
  // e.g. "### Web Search\nsome text" → "Web Search<SEP>some text"
  cleanText = cleanText.replace(/#{1,6}\s*(.+)/g, (_, heading) => {
    return heading.trim() + HEADING_SEP;
  });

  // Remove remaining markdown formatting
  cleanText = cleanText
    .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
    .replace(/\*(.*?)\*/g, '$1')     // Italic
    .replace(/`(.*?)`/g, '$1')      // Code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links
    .replace(/\n+/g, ' ')           // Replace newlines with spaces
    .replace(/\s+/g, ' ')           // Normalize whitespace
    .trim();

  return cleanText;
};

/**
 * Preprocess Hindi text for natural, Alexa/Siri-like prosody.
 * Inserts commas (= brief pauses) after common Hindi conjunctions and
 * discourse markers so the browser TTS engine breathes naturally.
 * Also normalises numbers and common abbreviations.
 * @param {string} text - Already cleaned Hindi text
 * @returns {string} - Prosody-enhanced Hindi text
 */
const preprocessHindiForSpeech = (text) => {
  if (!text) return text;

  let t = text;

  // ── 1. Insert a natural pause (comma) after key conjunctions / discourse markers
  //       when NOT already followed by punctuation.
  //       This mimics how Alexa/Siri breathe between clauses.
  const conjunctions = [
    'और', 'लेकिन', 'परंतु', 'किंतु', 'तो', 'क्योंकि', 'इसलिए',
    'इसलिये', 'जैसे', 'जब', 'जहाँ', 'जहां', 'यानी', 'यानि',
    'मतलब', 'अर्थात', 'हालांकि', 'हालाँकि', 'फिर भी', 'फिर',
    'इसके अलावा', 'इसके बाद', 'इसके साथ', 'साथ ही',
    'दूसरी ओर', 'एक तरफ', 'वहीं', 'तथापि', 'अतः'
  ];
  conjunctions.forEach(word => {
    // Add comma after the conjunction only if not already followed by , or । or .
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    t = t.replace(new RegExp(`(${escaped})\\s+(?![,।.])`, 'g'), `$1, `);
  });

  // ── 2. Ensure a natural pause after । (Devanagari full stop) — treat like .
  t = t.replace(/।\s*/g, '। ');

  // ── 3. Replace English digits with Devanagari-friendly phrasing so the
  //       Hindi voice engine reads them correctly (e.g. "3" → "तीन")
  const digitWords = ['शून्य', 'एक', 'दो', 'तीन', 'चार', 'पांच', 'छः', 'सात', 'आठ', 'नौ', 'दस'];
  t = t.replace(/\b([0-9]|10)\b/g, (_, n) => digitWords[parseInt(n)] || n);

  // ── 4. Expand common short-forms for more natural reading
  t = t
    .replace(/\bआदि\b/g, 'आदि,')         // etc.
    .replace(/\bवगैरह\b/g, 'वगैरह,')     // etc.
    .replace(/\bयानी\b/g, 'यानी,')       // i.e.
    .replace(/\bजैसे कि\b/g, 'जैसे कि,') // such as
    .replace(/\bउदाहरण\b/g, 'उदाहरण के लिए,'); // for example

  // ── 5. Normalise multiple spaces / commas
  t = t.replace(/,\s*,/g, ',').replace(/\s{2,}/g, ' ').trim();

  return t;
};


/**
 * Get the best available voice for speech
 * @returns {SpeechSynthesisVoice|null} - Best available voice or null
 */
const getBestVoice = () => {
  if (!isSupported) return null;

  const voices = window.speechSynthesis.getVoices();

  // Prefer English voices from Google, Microsoft, or default
  const preferredVoice = voices.find(voice =>
    voice.lang.startsWith('en') &&
    (voice.name.includes('Google') || voice.name.includes('Microsoft') || voice.default)
  );

  return preferredVoice || null;
};

/**
 * Check if text-to-speech is currently speaking
 * @returns {boolean} - True if currently speaking
 */
const isSpeaking = () => {
  return isSupported && window.speechSynthesis.speaking;
};

/**
 * Detect language script from text
 * @param {string} text 
 * @returns {string} language code
 */
const detectLanguage = (text) => {
  if (!text) return 'en';
  if (/[\u0900-\u097F]/.test(text)) return 'hi';     // Hindi / Marathi / Nepali / Sanskrit
  if (/[\u0600-\u06FF]/.test(text)) return 'ur';     // Urdu / Arabic
  if (/[\u0980-\u09FF]/.test(text)) return 'bn';     // Bengali / Assamese
  if (/[\u0C00-\u0C7F]/.test(text)) return 'te';     // Telugu
  if (/[\u0B80-\u0BFF]/.test(text)) return 'ta';     // Tamil
  if (/[\u0A80-\u0AFF]/.test(text)) return 'gu';     // Gujarati
  if (/[\u0C80-\u0CFF]/.test(text)) return 'kn';     // Kannada
  if (/[\u0D00-\u0D7F]/.test(text)) return 'ml';     // Malayalam
  if (/[\u0B00-\u0B7F]/.test(text)) return 'or';     // Odia
  if (/[\u0A00-\u0A7F]/.test(text)) return 'pa';     // Punjabi
  return 'en';
};

/**
 * Get the best voice based on the text language, strictly enforcing a lady's voice.
 * @param {string} text 
 * @param {string} [voiceName] 
 * @returns {SpeechSynthesisVoice|null}
 */
const getVoiceForText = (text, voiceName) => {
  if (!isSupported) return null;
  let voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return null;

  // Extremely strict exclusion of any voice perceived as male
  const isMale = (name) => {
    const ln = name.toLowerCase();
    // Words and names denoting male voices on Google/Apple/Microsoft TTS
    return /\bmale\b/.test(ln) || ['david', 'mark', 'ravi', 'ryan', 'brian', 'madhur', 'deep', 'amit', 'james', 'william', 'aaron', 'arthur', 'daniel', 'rishi', 'hemant', 'prabhat', 'cmn', 'boy', 'man'].some(n => ln.includes(n));
  };

  // Keep only voices that are NOT male
  voices = voices.filter(v => !isMale(v.name));

  // Helper to find an explicitly verified female voice
  // Includes top Microsoft/Google Hindi natural voices for best quality
  const isFemale = (v) => {
    const ladyNames = [
      'female', 'women', 'girl',
      // English
      'zira', 'samantha', 'serena', 'karen', 'victoria', 'tessa', 'moira', 'eva', 'allison', 'ava',
      // Indian / Hindi
      'veena', 'neerja', 'swara', 'heera', 'sangeeta', 'kalpana', 'anjali', 'lekha',
      // Natural/Neural variants
      'natural'
    ];
    return ladyNames.some(n => v.name.toLowerCase().includes(n));
  };

  if (voiceName) {
    const matched = voices.find(v => v.name.toLowerCase().includes(voiceName.toLowerCase()));
    if (matched) return matched;
  }

  const langCode = detectLanguage(text);

  if (langCode !== 'en') {
    const regionalVoices = voices.filter(v => v.lang.startsWith(langCode));
    if (regionalVoices.length > 0) {
      // For Hindi: prefer Microsoft Swara first (closest to neural/Alexa quality)
      if (langCode === 'hi') {
        const swara = regionalVoices.find(v => v.name.toLowerCase().includes('swara'));
        if (swara) return swara;
        const googleHindi = regionalVoices.find(v => v.name.toLowerCase().includes('google'));
        if (googleHindi) return googleHindi;
      }

      // Prioritize high-quality natural/premium voices
      const premiumRegional = regionalVoices.filter(v => v.name.toLowerCase().includes('natural') || v.name.toLowerCase().includes('premium'));
      const pool = premiumRegional.length > 0 ? premiumRegional : regionalVoices;

      // Must prefer explicit female first
      const explicitFemale = pool.find(isFemale);
      if (explicitFemale) return explicitFemale;

      // Otherwise take google/microsoft preset (since we already stripped male names)
      const preferredRegional = pool.find(v =>
        v.name.includes('Google') || v.name.includes('Microsoft') || v.default
      );
      return preferredRegional || pool[0];
    }
  }

  // Prefer Indian English for Hinglish/English fallback
  const indianEnglishVoices = voices.filter(v => v.lang === 'en-IN' || v.lang === 'en_IN');
  if (indianEnglishVoices.length > 0) {
    const premiumIndian = indianEnglishVoices.filter(v => v.name.toLowerCase().includes('natural') || v.name.toLowerCase().includes('premium'));
    const pool = premiumIndian.length > 0 ? premiumIndian : indianEnglishVoices;

    const explicitFemale = pool.find(isFemale);
    if (explicitFemale) return explicitFemale;

    const preferredIndian = pool.find(v =>
      v.name.includes('Google') || v.name.includes('Microsoft')
    );
    if (preferredIndian) return preferredIndian;
    return pool[0];
  }

  // If no regional/Indian voice found, aggressively fallback to strict English female preset lists
  const preferredNames = [
    "Google UK English Female",
    "Google US English Female",
    "Microsoft Zira",
    "Samantha",
    "Serena",
    "Karen",
    "Victoria",
    "Tessa",
    "Moira"
  ];

  for (const name of preferredNames) {
    const match = voices.find(v => v.name.includes(name));
    if (match) return match;
  }

  // Final fallback: forcibly pick an explicitly female voice from ANY language/genre if nothing else matches
  const anyExplicitFemale = voices.find(isFemale);
  if (anyExplicitFemale) return anyExplicitFemale;

  // Absolute last resort
  return voices.find(v =>
    v.lang.startsWith('en') &&
    (v.name.includes('Google') || v.name.includes('Microsoft') || v.default)
  ) || voices[0] || null;
};

/**
 * Split text into chunks suitable for speech synthesis
 * @param {string} text - Text to split
 * @param {number} maxChunkLength - Maximum characters per chunk (default: 3000)
 * @returns {string[]} - Array of text chunks
 */
const splitTextIntoChunks = (text, maxChunkLength = CHUNK_SIZE) => {
  if (!text || text.length <= maxChunkLength) {
    return [text];
  }

  const chunks = [];
  let remainingText = text;

  while (remainingText.length > 0) {
    let chunkEnd = maxChunkLength;

    // If we're not at the end, try to find a good breaking point
    if (remainingText.length > maxChunkLength) {
      // Prefer to break at sentence endings
      const sentenceBreaks = ['. ', '! ', '? ', '\n'];
      for (const breakPoint of sentenceBreaks) {
        const lastBreak = remainingText.lastIndexOf(breakPoint, maxChunkLength);
        if (lastBreak > maxChunkLength * 0.7) { // Only use if it's not too close to start
          chunkEnd = lastBreak + breakPoint.length;
          break;
        }
      }

      // If no good sentence break found, try word boundaries
      if (chunkEnd === maxChunkLength) {
        const lastSpace = remainingText.lastIndexOf(' ', maxChunkLength);
        if (lastSpace > maxChunkLength * 0.8) { // Only use if not too close to start
          chunkEnd = lastSpace;
        }
      }
    }

    const chunk = remainingText.substring(0, chunkEnd).trim();
    if (chunk) {
      chunks.push(chunk);
    }

    remainingText = remainingText.substring(chunkEnd).trim();
  }

  return chunks;
};

/**
 * Speak the given text with optional configuration.
 * When the text contains ### headings (TalentPulse sections), a 0.1-second
 * pause is inserted after each heading before the body text is spoken.
 * @param {string} text - Text to speak
 * @param {Object} options - Speech options
 * @param {number} options.rate - Speech rate (0.1 to 10, default: 1.0)
 * @param {number} options.pitch - Speech pitch (0 to 2, default: 1.0)
 * @param {number} options.volume - Speech volume (0 to 1, default: 1.0)
 * @param {number} options.headingPauseMs - Pause (ms) after heading (default: 100)
 * @param {Function} options.onStart - Callback when speech starts
 * @param {Function} options.onEnd - Callback when speech ends
 * @param {Function} options.onError - Callback when speech errors
 * @returns {boolean} - True if speech started successfully
 */
const speak = (text, options = {}) => {
  // Check browser support with detailed feedback
  if (!isSupported) {
    const compatibility = getCompatibilityInfo();
    let errorMessage = 'Text-to-Speech not supported in this browser.';

    if (compatibility.isFirefox) {
      errorMessage += ' Firefox has limited TTS support. Try Chrome, Safari, or Edge for better experience.';
    } else if (compatibility.isMobile) {
      errorMessage += ' Try using Chrome or Safari on mobile devices.';
    } else {
      errorMessage += ' Please use a modern browser like Chrome, Safari, or Edge.';
    }

    if (options.onError) {
      options.onError(new Error(errorMessage));
    }
    return false;
  }

  // Stop any current speech unless explicitly told not to
  if (options.interrupt !== false) {
    stop();
  }

  // Check text length limits
  if (text && text.length > MAX_TEXT_LENGTH) {
    const errorMessage = `Text is too long (${text.length} characters). Maximum supported length is ${MAX_TEXT_LENGTH} characters. Please try with shorter text.`;
    if (options.onError) {
      options.onError(new Error(errorMessage));
    }
    return false;
  }

  // Clean the text (headings are preserved as HEADING_SEP sentinels)
  const cleanText = cleanTextForSpeech(text);

  if (!cleanText) {
    if (options.onError) {
      options.onError(new Error('No text to speak after cleaning'));
    }
    return false;
  }

  // If the cleaned text contains heading sentinels, split into
  // [heading, body, heading, body, ...] segments and speak with pauses.
  if (cleanText.includes(HEADING_SEP)) {
    return speakWithHeadingPauses(cleanText, options);
  }

  // Split text into chunks for better performance with long content
  const chunks = splitTextIntoChunks(cleanText, CHUNK_SIZE);

  // If only one chunk, use the original single utterance approach
  if (chunks.length === 1) {
    return speakSingleChunk(chunks[0], options);
  }

  // For multiple chunks, play them sequentially
  return speakMultipleChunks(chunks, options);
};

/**
 * Internal: speak text that contains HEADING_SEP delimiters.
 * Each heading is spoken, then a pause of `options.headingPauseMs` (default 100ms),
 * then its body text — giving users a clear audio break between sections.
 */
const speakWithHeadingPauses = (cleanText, options = {}) => {
  const pauseMs = options.headingPauseMs !== undefined ? options.headingPauseMs : 100;

  // Reset the module-level cancel flag for this new session
  currentSessionCancelled = false;

  const rawSegments = cleanText.split(HEADING_SEP);


  const speechQueue = []; // { text, pauseAfterMs? }

  rawSegments.forEach((segment, idx) => {
    const trimmed = segment.trim();
    if (!trimmed) return;

    const isLastSegment = idx === rawSegments.length - 1;

    if (!isLastSegment) {
      // This segment ends with a heading (the heading text itself).
      // Everything before the last sentence-break is preamble body text;
      // the last sentence is the heading.
      // Find the last sentence break in this segment.
      const lastBreakMatch = trimmed.match(/^([\s\S]*?)([^.!?]*[^.!?\s][^.!?]*)$/);
      let preamble = '';
      let headingText = trimmed;
      if (lastBreakMatch && lastBreakMatch[1].trim()) {
        preamble = lastBreakMatch[1].trim();
        headingText = lastBreakMatch[2].trim();
      }

      if (preamble) {
        // Speak preamble body first (no extra pause)
        splitTextIntoChunks(preamble, CHUNK_SIZE).forEach(c => {
          if (c.trim()) speechQueue.push({ text: c.trim() });
        });
      }

      if (headingText) {
        // Speak heading, then mark a pause after it
        speechQueue.push({ text: headingText, pauseAfterMs: pauseMs });
      }
    } else {
      // Last segment — pure body text
      splitTextIntoChunks(trimmed, CHUNK_SIZE).forEach(c => {
        if (c.trim()) speechQueue.push({ text: c.trim() });
      });
    }
  });

  if (speechQueue.length === 0) {
    if (options.onError) options.onError(new Error('No text to speak after cleaning'));
    return false;
  }

  let currentIndex = 0;
  let hasStarted = false;

  const speakNext = () => {
    if (currentSessionCancelled) return;
    if (currentIndex >= speechQueue.length) {
      currentUtterance = null;
      if (options.onEnd) options.onEnd();
      return;
    }

    const item = speechQueue[currentIndex];
    currentIndex++;

    if (!item.text || item.text.trim().length === 0) {
      speakNext();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(item.text);
    // Unified Alexa/Siri-like tone for ALL languages: confident pace, neutral pitch, full volume
    utterance.rate = options.rate || 0.92;
    utterance.pitch = options.pitch || 1.0;
    utterance.volume = options.volume || 1.0;

    const detectedLang = detectLanguage(item.text);
    if (detectedLang !== 'en') {
      const langMap = {
        'hi': 'hi-IN', 'ur': 'ur-IN', 'bn': 'bn-IN', 'te': 'te-IN',
        'ta': 'ta-IN', 'gu': 'gu-IN', 'kn': 'kn-IN', 'ml': 'ml-IN',
        'or': 'or-IN', 'pa': 'pa-IN'
      };
      utterance.lang = langMap[detectedLang] || detectedLang;
    } else {
      utterance.lang = 'en-IN';
    }

    const voice = getVoiceForText(item.text, options.voiceName);
    // Apply Hindi prosody preprocessing for Alexa/Siri-like naturalness
    if (detectedLang === 'hi') {
      utterance.text = preprocessHindiForSpeech(utterance.text);
    }
    if (voice) utterance.voice = voice;

    utterance.onstart = () => {
      if (!hasStarted && options.onStart) {
        hasStarted = true;
        options.onStart();
      }
    };

    utterance.onend = () => {
      if (utterance._watchdog) clearTimeout(utterance._watchdog);
      if (currentSessionCancelled) return;
      const delay = item.pauseAfterMs != null ? item.pauseAfterMs : 0;
      setTimeout(speakNext, delay);
    };

    utterance.onerror = (event) => {
      if (utterance._watchdog) clearTimeout(utterance._watchdog);
      currentSessionCancelled = true;
      currentUtterance = null;
      if (options.onError) options.onError(event);
    };

    currentUtterance = utterance;
    window.speechSynthesis.speak(utterance);

    const watchdogDuration = Math.max(5000, utterance.text.length * 200);
    utterance._watchdog = setTimeout(() => {
      if (currentUtterance === utterance && window.speechSynthesis.speaking) {
        console.warn("TTS watchdog triggered for heading pause segment");
        window.speechSynthesis.cancel();
        if (utterance.onend) utterance.onend();
      }
    }, watchdogDuration);
  };

  speakNext();
  return true;
};

/**
 * Speak a single chunk of text
 */
const speakSingleChunk = (text, options = {}) => {
  // Reset the module-level cancel flag so onEnd callbacks aren't silently blocked
  currentSessionCancelled = false;

  // Create speech utterance
  const utterance = new SpeechSynthesisUtterance(text);

  // Unified Alexa/Siri-like tone for ALL languages: confident pace, neutral pitch, full volume
  utterance.rate = options.rate || 0.92;
  utterance.pitch = options.pitch || 1.0;
  utterance.volume = options.volume || 1.0;

  // Set language and voice based on text
  const detectedLang = detectLanguage(text);
  if (detectedLang !== 'en') {
    const langMap = {
      'hi': 'hi-IN', 'ur': 'ur-IN', 'bn': 'bn-IN', 'te': 'te-IN',
      'ta': 'ta-IN', 'gu': 'gu-IN', 'kn': 'kn-IN', 'ml': 'ml-IN',
      'or': 'or-IN', 'pa': 'pa-IN'
    };
    utterance.lang = langMap[detectedLang] || detectedLang;
  } else {
    // Default to en-IN for Indian-context platforms for richer transliteration handling
    utterance.lang = 'en-IN';
  }

  const voice = getVoiceForText(text, options.voiceName);
  // Apply Hindi prosody preprocessing for Alexa/Siri-like naturalness
  if (detectLanguage(text) === 'hi') {
    utterance.text = preprocessHindiForSpeech(utterance.text);
  }
  if (voice) {
    utterance.voice = voice;
  }

  // Set event handlers
  utterance.onstart = () => {
    if (options.onStart) options.onStart();
  };

  utterance.onend = () => {
    if (utterance._watchdog) clearTimeout(utterance._watchdog);
    currentUtterance = null;
    if (!currentSessionCancelled && options.onEnd) options.onEnd();
  };

  utterance.onerror = (event) => {
    if (utterance._watchdog) clearTimeout(utterance._watchdog);
    currentUtterance = null;
    if (options.onError) options.onError(event);
  };

  // Store current utterance
  currentUtterance = utterance;

  // Start speaking
  window.speechSynthesis.speak(utterance);

  const watchdogDuration = Math.max(5000, utterance.text.length * 200);
  utterance._watchdog = setTimeout(() => {
    if (currentUtterance === utterance && window.speechSynthesis.speaking) {
      console.warn("TTS watchdog triggered for single chunk");
      window.speechSynthesis.cancel();
      if (utterance.onend) utterance.onend();
    }
  }, watchdogDuration);

  return true;
};

/**
 * Speak multiple chunks sequentially
 */
const speakMultipleChunks = (chunks, options = {}) => {
  // Reset the module-level cancel flag so onEnd callbacks aren't silently blocked
  currentSessionCancelled = false;

  let currentChunkIndex = 0;
  let hasStarted = false;
  let hasError = false;

  const speakNextChunk = () => {
    // If we encountered an error, stop processing
    if (hasError || currentSessionCancelled) return;

    if (currentChunkIndex >= chunks.length) {
      // All chunks completed
      currentUtterance = null;
      if (options.onEnd) options.onEnd();
      return;
    }

    const chunk = chunks[currentChunkIndex];

    // Skip empty chunks
    if (!chunk || chunk.trim().length === 0) {
      currentChunkIndex++;
      speakNextChunk();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(chunk);

    // Unified Alexa/Siri-like tone for ALL languages: confident pace, neutral pitch, full volume
    utterance.rate = options.rate || 0.92;
    utterance.pitch = options.pitch || 1.0;
    utterance.volume = options.volume || 1.0;

    // Set language and voice based on chunk
    const detectedLang = detectLanguage(chunk);
    if (detectedLang !== 'en') {
      const langMap = {
        'hi': 'hi-IN', 'ur': 'ur-IN', 'bn': 'bn-IN', 'te': 'te-IN',
        'ta': 'ta-IN', 'gu': 'gu-IN', 'kn': 'kn-IN', 'ml': 'ml-IN',
        'or': 'or-IN', 'pa': 'pa-IN'
      };
      utterance.lang = langMap[detectedLang] || detectedLang;
    } else {
      utterance.lang = 'en-IN';
    }

    const voice = getVoiceForText(chunk, options.voiceName);
    // Apply Hindi prosody preprocessing for Alexa/Siri-like naturalness
    if (detectedLang === 'hi') {
      utterance.text = preprocessHindiForSpeech(utterance.text);
    }
    if (voice) {
      utterance.voice = voice;
    }

    // Event handlers
    utterance.onstart = () => {
      if (!hasStarted && options.onStart) {
        hasStarted = true;
        options.onStart();
      }
    };

    utterance.onend = () => {
      if (utterance._watchdog) clearTimeout(utterance._watchdog);
      if (currentSessionCancelled) return;
      currentChunkIndex++;
      // Small pause between chunks (100ms) for natural speech flow
      setTimeout(speakNextChunk, 100);
    };

    utterance.onerror = (event) => {
      if (utterance._watchdog) clearTimeout(utterance._watchdog);
      hasError = true;
      currentUtterance = null;
      const errorMessage = `Speech synthesis failed at chunk ${currentChunkIndex + 1}/${chunks.length}. ${event.error || 'Unknown error'}`;
      if (options.onError) {
        options.onError(new Error(errorMessage));
      }
    };

    currentUtterance = utterance;
    window.speechSynthesis.speak(utterance);

    const watchdogDuration = Math.max(5000, utterance.text.length * 200);
    utterance._watchdog = setTimeout(() => {
      if (currentUtterance === utterance && window.speechSynthesis.speaking) {
        console.warn("TTS watchdog triggered for multiple chunks");
        window.speechSynthesis.cancel();
        if (utterance.onend) utterance.onend();
      }
    }, watchdogDuration);
  };

  // Start with first chunk
  speakNextChunk();
  return true;
};

/**
 * Toggle speech for the given text
 * If currently speaking the same text, stop it
 * If not speaking or speaking different text, start speaking the new text
 * @param {string} text - Text to speak
 * @param {Object} options - Speech options (same as speak method)
 * @returns {boolean} - True if started speaking, false if stopped
 */
const toggle = (text, options = {}) => {
  if (isSpeaking()) {
    stop();
    return false;
  } else {
    return speak(text, options);
  }
};

// Export the functions as a module
const textToSpeech = {
  speak,
  stop,
  toggle,
  isSpeaking,
  cleanTextForSpeech,
  getBestVoice,
  getCompatibilityInfo,
  splitTextIntoChunks,
};

/**
 * Compute a "MM:SS" duration string from the spoken text.
 * Uses cleanTextForSpeech to strip headings, HTML, markdown — same as the TTS engine.
 * Call this ONCE on the full final text (e.g. at stream-end) to avoid timing issues.
 *
 * @param {string} text - Raw assistant response content
 * @returns {string} "MM:SS" e.g. "00:17"
 */
export const computeSpokenDuration = (text) => {
  if (!text) return "--:--";
  const clean = cleanTextForSpeech(text);

  // Digits (e.g. 10-digit mobile numbers or salaries) take much longer to pronounce 
  // than their literal character count implies. Add 4 "virtual characters" per digit 
  // since the word form of a number (e.g., "seven") averages 4-5 letters.
  const digitCount = (clean.match(/\d/g) || []).length;
  const effectiveLength = clean.length + (digitCount * 4);

  // Using 13 chars/sec divisor to better match the actual speech pacing on long blocks
  const seconds = Math.max(3, Math.round(effectiveLength / 13));
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export default textToSpeech;

